import { OrderType, PublicKey, ZERO } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	UISerializableOrder,
} from '@velocity-exchange/common';
import {
	MarketOrderToastId,
	MarketOrderToastOrderInfo,
	MarketOrderToastStateTransitionEvent,
} from './MarketOrderToastStateTypes';
import {
	OrderId,
	V2MarketOrderToastIdHandler,
} from './MarketOrderToastIdHandler';

export function isRelevantMarketOrder(order: UISerializableOrder): boolean {
	return (
		ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.ORACLE)
	);
}

export function findToastIdForOrder(
	order: UISerializableOrder,
	user: PublicKey
): MarketOrderToastId | null {
	// orderId can only be 1 or greater, hence we return if 0 or undefined
	if (!order.orderId || order.orderId === 0) {
		return null;
	}

	const marketId = new MarketId(order.marketIndex, order.marketType);
	const toastId =
		V2MarketOrderToastIdHandler.getToastIdToMatchWithOrderStateRead({
			user,
			orderId: order.orderId as OrderId,
			marketId,
		});

	return toastId;
}

export function convertUISerializableOrderToEvent(
	order: UISerializableOrder,
	marketId: MarketId,
	toastId: MarketOrderToastId
): MarketOrderToastStateTransitionEvent {
	return {
		type: 'order_state_read',
		toastId,
		data: {
			marketId,
			orderId: order.orderId,
			userOrderId: order.userOrderId,
			cumulativeQuoteAmountFilled: order.quoteAssetAmountFilled.val,
			cumulativeBaseAmountFilled: order.baseAssetAmountFilled.val,
			baseAmountOrdered: order.baseAssetAmount.val,
			direction: order.direction,
			status: order.status,
			auctionStartSlot: order.slot?.toNumber(),
			auctionDuration: order.auctionDuration,
			maxTs: order.maxTs,
		},
	};
}

export function createInitialOrderInfoFromEvent(
	event: MarketOrderToastStateTransitionEvent,
	toastId: MarketOrderToastId,
	additionalProps?: Partial<MarketOrderToastOrderInfo>
): MarketOrderToastOrderInfo {
	return {
		marketId: event.data.marketId!,
		toastId,
		orderId: event.data.orderId ?? 0,
		userOrderId: event.data.userOrderId ?? 0,
		cumulativeQuoteAmountFilled: event.data.cumulativeQuoteAmountFilled ?? ZERO,
		cumulativeBaseAmountFilled: event.data.cumulativeBaseAmountFilled ?? ZERO,
		baseAmountOrdered: event.data.baseAmountOrdered ?? ZERO,
		auctionEnabled: event.data.auctionEnabled ?? false,
		direction: event.data.direction!,
		lastAction: event.data.lastAction!,
		lastActionExplanation: event.data.lastActionExplanation!,
		orderRoute: event.data.orderRoute,
		originalTradeFormDetails: event.data.originalTradeFormDetails,
		toastInitBlockHeight: event.data.toastInitBlockHeight,
		stepSize: event.data.stepSize,
		customSignMessage: event.data.customSignMessage,
		includesSlOrder: event.data.includesSlOrder,
		includesTpOrder: event.data.includesTpOrder,
		auctionStartSlot: event.data.auctionStartSlot,
		auctionDuration: event.data.auctionDuration,
		maxTs: event.data.maxTs,
		...additionalProps,
	};
}
